/**
 * Zod schemas mirroring the canonical TypeScript types.
 *
 * Use these at runtime boundaries: webhook normalizers, API route handlers,
 * AI output parser, queue consumer. Static types live in `./types.ts` (and
 * the schemas below `.infer<typeof X>` is equivalent to the static type — we
 * use the static type for ergonomics but the schemas for validation).
 */

import { z } from "zod";
import {
  CHANNELS,
  SOURCE_PLATFORMS,
  SOURCE_CHANNELS,
  CONTACT_STATUSES,
  AWARENESS_LEVELS,
  LEAD_TEMPERATURES,
  AI_INTENTS,
  AI_SUGGESTED_NEXT_ACTIONS,
  AI_MISSING_FIELDS,
  MEDIA_TYPES,
  ACTIVITY_LOG_EVENT_TYPES,
  HANDOFF_TOKEN_PREFIX,
} from "./constants.js";

// ---------------------------------------------------------------------------
// UnifiedInboundMessage
// ---------------------------------------------------------------------------

/**
 * Cross-Channel WhatsApp Handoff attribution block. Populated by the
 * WAHA normalizer (Phase H2) when it detects a `[#hf_xxx]` or
 * `[#ref:xxx]` marker in the inbound text.
 *
 * The handoff token format is enforced loosely here (prefix + alphanumeric)
 * because the canonical regex lives in
 * `HANDOFF_MARKER_RE` (constants.ts) and is used at the extraction point;
 * the schema only guards against grossly malformed values crossing the
 * boundary.
 */
export const UnifiedInboundAttributionSchema = z.object({
  sourceChannel: z.enum(SOURCE_CHANNELS).optional(),
  campaignId: z.string().max(120).optional(),
  ref: z.string().max(255).optional(),
  handoffToken: z
    .string()
    .min(HANDOFF_TOKEN_PREFIX.length + 8)
    .max(48)
    .startsWith(HANDOFF_TOKEN_PREFIX)
    .optional(),
});

export const UnifiedInboundMessageSchema = z.object({
  channel: z.enum(CHANNELS),
  externalMessageId: z.string().min(1).max(255),
  externalThreadId: z.string().min(1).max(255),
  senderExternalId: z.string().min(1).max(255),
  senderName: z.string().max(255).optional(),
  senderUsername: z.string().max(120).optional(),
  senderPhone: z.string().max(40).optional(),
  recipientExternalId: z.string().max(255).optional(),
  messageText: z.string().optional(),
  mediaUrl: z.string().url().optional(),
  mediaType: z.enum(MEDIA_TYPES).optional(),
  sourceListing: z.string().max(255).optional(),
  sourcePropertyId: z.string().max(26).optional(),
  sourcePlatform: z.enum(SOURCE_PLATFORMS).optional(),
  attribution: UnifiedInboundAttributionSchema.optional(),
  timestamp: z.string().min(1),
  rawPayload: z.unknown(),
});

// ---------------------------------------------------------------------------
// AI response JSON
// ---------------------------------------------------------------------------

// LLMs frequently OMIT fields they don't know rather than emitting them as
// `null`. We treat missing == null so the parser doesn't throw and silently
// drop the reply. `default(null)` runs after `nullable()`, normalising the
// shape downstream code consumes.
export const AIExtractedFieldsSchema = z.object({
  // The lead's first name (or full name). Captured from the conversation
  // when the lead introduces themselves or answers the bot's "what's your
  // name" prompt. Backend persists it to `contacts.display_name` so future
  // turns show the lead by name in the dashboard.
  name: z.string().min(1).max(120).nullable().default(null),
  // Email address captured when the bot asks for one before confirming a
  // booking. Persisted to `contacts.primary_email` for future confirmations.
  // Validated loosely — LLMs sometimes paste with a trailing period; we
  // accept and clean up server-side rather than throwing.
  email: z
    .string()
    .max(255)
    .email()
    .nullable()
    .default(null)
    .catch(null),
  budget_min_idr: z.number().int().nullable().default(null),
  budget_max_idr: z.number().int().nullable().default(null),
  move_in_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "must be YYYY-MM-DD")
    .nullable()
    .default(null),
  length_of_stay_months: z.number().int().nullable().default(null),
  preferred_area: z.string().nullable().default(null),
  bedrooms_needed: z.number().int().nullable().default(null),
  property_name: z.string().nullable().default(null),
  property_id: z.string().nullable().default(null),
});

/**
 * Visit confirmation block — see `AIVisitConfirmation` in types.ts.
 * The whole field is nullable at the AIResponseJSON level so the model
 * can omit it on every turn that isn't a booking confirmation.
 */
export const AIVisitConfirmationSchema = z.object({
  confirmed: z.boolean(),
  scheduled_for: z
    .string()
    .regex(
      /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/,
      "must be 'YYYY-MM-DD HH:mm' in business local time",
    ),
  visit_type: z.enum(["physical", "virtual"]).default("physical"),
  party_size: z.number().int().min(1).max(100).nullable().default(null),
  notes: z.string().max(500).nullable().default(null),
});

// Top-level forgiving defaults too — the only thing we absolutely require
// is a non-empty `text` (the actual reply we send) and `intent` (used for
// scoring / routing). Everything else has a safe default.
export const AIResponseJSONSchema = z.object({
  text: z.string().min(1),
  intent: z.enum(AI_INTENTS),
  awareness_level: z.enum(AWARENESS_LEVELS).default("UNAWARE"),
  temperature: z.enum(LEAD_TEMPERATURES).default("cold"),
  score_delta: z.number().int().min(-40).max(40).default(0),
  // Vertical templates (restaurant, spa, dentist, cafe, villa-rental, etc.)
  // each define their own qualification field keys via the runtime
  // `qualification_fields` table. To support that without bloating a shared
  // enum, missing_fields is a free-form string array. The dashboard uses
  // these only for display; nothing typed reads them.
  missing_fields: z.array(z.string()).default([]),
  extracted: AIExtractedFieldsSchema.default({} as never),
  should_escalate: z.boolean().default(false),
  escalation_reason: z.string().nullable().default(null),
  suggested_next_action: z.enum(AI_SUGGESTED_NEXT_ACTIONS).default("continue_qualification"),
  visit_confirmation: AIVisitConfirmationSchema.nullable().default(null),
});

// ---------------------------------------------------------------------------
// Activity log input
// ---------------------------------------------------------------------------

export const ActivityLogInputSchema = z.object({
  businessId: z.string().nullable(),
  contactId: z.string().nullable().optional(),
  conversationId: z.string().nullable().optional(),
  actorType: z.enum(["ai", "human", "system"]),
  actorId: z.string().nullable().optional(),
  eventType: z.enum(ACTIVITY_LOG_EVENT_TYPES),
  title: z.string().max(255).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

// ---------------------------------------------------------------------------
// Cross-Channel WhatsApp Handoff API (Phase H1+)
//
// These schemas back the upcoming endpoints (Phase H3+):
//   - POST /api/handoffs/whatsapp-link → HandoffCreateInputSchema
//   - GET  /api/contacts/:id/memory   → ContactMemoryResponseSchema
//
// They are defined in H1 so the worker and dashboard can already share the
// contract while the services land.
// ---------------------------------------------------------------------------

/**
 * Body of `POST /api/handoffs/whatsapp-link`.
 *
 * Two operating modes:
 *
 *   - `"handoff"` (default when `sourceContactId` or `sourceConversationId`
 *     is provided): the route creates a `channel_handoffs` row and returns
 *     a wa.me URL with a `[#hf_<token>]` marker. Future WhatsApp inbound
 *     bearing that marker will atomically attach to the originating
 *     contact / conversation (Phase H4 — identity resolver).
 *   - `"ref_only"`: no DB row, no token. The returned wa.me URL only
 *     carries `[#ref:...]`, `[#source:...]`, `[#campaign:...]`,
 *     `[#property:...]`, `[#listing:...]` markers for source attribution.
 *
 * Field naming: API body uses `sourceContactId` / `sourceConversationId`
 * (the click's source conversation, conceptually). The persistence layer
 * stores them under their canonical DB names
 * `originating_contact_id` / `originating_conversation_id`.
 */
export const HandoffCreateInputSchema = z.object({
  mode: z.enum(["handoff", "ref_only"]).optional(),
  businessId: z.string().length(26).optional(),
  sourceChannel: z.enum(SOURCE_CHANNELS),
  sourceContactId: z.string().length(26).optional(),
  sourceConversationId: z.string().length(26).optional(),
  sourcePropertyId: z.string().length(26).optional(),
  sourceListing: z.string().max(255).optional(),
  campaignId: z.string().max(120).optional(),
  ref: z.string().max(255).optional(),
  targetPhone: z.string().max(40).optional(),
  message: z.string().max(1024).optional(),
  ttlHours: z.number().int().min(1).max(720).optional(),
});

/** Response when `mode === "handoff"`. */
export const HandoffCreateHandoffResponseSchema = z.object({
  mode: z.literal("handoff"),
  waUrl: z.string().url(),
  handoffToken: z.string().startsWith(HANDOFF_TOKEN_PREFIX).max(48),
  expiresAt: z.string().min(1),
  ref: z.string().nullable(),
  campaignId: z.string().nullable(),
});

/** Response when `mode === "ref_only"`. */
export const HandoffCreateRefOnlyResponseSchema = z.object({
  mode: z.literal("ref_only"),
  waUrl: z.string().url(),
  ref: z.string().nullable(),
  campaignId: z.string().nullable(),
});

/** Discriminated by `mode`. Use at the API boundary on the dashboard side. */
export const HandoffCreateResponseSchema = z.discriminatedUnion("mode", [
  HandoffCreateHandoffResponseSchema,
  HandoffCreateRefOnlyResponseSchema,
]);

/**
 * Response of `GET /api/handoffs/:token`. Token is masked so the secret
 * never reappears in admin UI / proxy logs.
 */
export const SafeHandoffStatusSchema = z.object({
  tokenMasked: z.string(),
  status: z.enum(["active", "consumed", "expired", "revoked"]),
  sourceChannel: z.string(),
  sourceListing: z.string().nullable(),
  sourcePropertyId: z.string().nullable(),
  campaignId: z.string().nullable(),
  ref: z.string().nullable(),
  expiresAt: z.string(),
  createdAt: z.string(),
  consumedAt: z.string().nullable(),
  originatingContactId: z.string().nullable(),
  originatingConversationId: z.string().nullable(),
  consumedByConversationId: z.string().nullable(),
});

/**
 * Response shape of `GET /api/contacts/:id/memory`. Mirrors the
 * `CrossChannelContext` type in `./types.ts`. Defined as a Zod schema for
 * runtime validation on the dashboard side.
 */
export const ContactMemoryIdentitySchema = z.object({
  channel: z.enum(CHANNELS),
  externalId: z.string(),
  phone: z.string().nullable(),
  displayName: z.string().nullable(),
  createdAt: z.string(),
});

export const ContactMemoryRelatedConversationSchema = z.object({
  id: z.string(),
  channel: z.enum(CHANNELS),
  lastMessageAt: z.string().nullable(),
  sourceChannel: z.string().nullable(),
  summary: z
    .object({
      id: z.string(),
      conversationId: z.string(),
      summaryText: z.string(),
      keyFacts: z.record(z.string(), z.unknown()).nullable(),
      turnCount: z.number().int(),
      model: z.string().nullable(),
      generatedAt: z.string(),
      generatedBy: z.enum(["ai", "human", "system"]),
      triggerKind: z.enum([
        "on_handoff",
        "periodic",
        "on_takeover",
        "manual",
        "on_close",
      ]),
    })
    .nullable(),
});

export const ContactMemoryInterestSchema = z.object({
  id: z.string(),
  contactId: z.string(),
  propertyId: z.string().nullable(),
  propertyName: z.string().nullable(),
  signal: z.enum([
    "mentioned",
    "asked_availability",
    "requested_photos",
    "requested_pricing",
    "requested_visit",
    "rejected",
  ]),
  confidence: z.number().int(),
  sourceConversationId: z.string().nullable(),
  lastSignalAt: z.string(),
});

export const ContactMemoryNextActionSchema = z.object({
  id: z.string(),
  contactId: z.string(),
  conversationId: z.string().nullable(),
  action: z.string(),
  targetPropertyId: z.string().nullable(),
  dueAt: z.string().nullable(),
  status: z.enum(["pending", "completed", "superseded", "cancelled"]),
  source: z.enum(["ai", "human", "system"]),
  createdAt: z.string(),
});

export const ContactMemoryResponseSchema = z.object({
  contact: z.object({
    id: z.string(),
    displayName: z.string().nullable(),
    primaryPhone: z.string().nullable(),
    status: z.enum(CONTACT_STATUSES),
    score: z.number().int(),
    awarenessLevel: z.enum(AWARENESS_LEVELS),
    temperature: z.enum(LEAD_TEMPERATURES),
    preferredContactChannel: z.enum(CHANNELS).nullable(),
  }),
  identities: z.array(ContactMemoryIdentitySchema),
  relatedConversations: z.array(ContactMemoryRelatedConversationSchema),
  lastKnownProperty: z
    .object({
      id: z.string().nullable(),
      name: z.string().nullable(),
    })
    .nullable(),
  lastObjection: z.string().nullable(),
  interests: z.array(ContactMemoryInterestSchema),
  pendingNextAction: ContactMemoryNextActionSchema.nullable(),
  qualifications: z.object({
    budget_min_idr: z.number().int().nullable(),
    budget_max_idr: z.number().int().nullable(),
    move_in_date: z.string().nullable(),
    length_of_stay_months: z.number().int().nullable(),
    preferred_area: z.string().nullable(),
    bedrooms_needed: z.number().int().nullable(),
  }),
});
